import { useState, useReducer, useCallback, useEffect } from 'react';
import { useDeepCompareEffect } from 'ahooks';
import { debounce as _debounce } from 'lodash-es';
import { history } from 'umi';
import {
  getMobilePageData,
  IMarketProfileMobilePageData,
} from '@intelligence/services/marketProfile';
// import { formatNum, formatPercentNum } from '@/pages/intelligence/utils/commonUtils';
import { IMobileGameTableHooksProps } from '../type';
import { ALL_PLATFORMS, MAX_CHECKED_NUMBER, ALL_METRICS } from '../constants';
import { ECHARTSCOLOR } from '@intelligence/utils/echartsConst';
import { CountryItem } from '@intelligence/pages/MarketProfile/hooks/useCountriesHooks';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import { feachTimeTypeToFilterTimeType } from '@/pages/intelligence/utils/common';

const reducer = (state, { type, payload }) => ({
  ...state,
  [type]: payload,
});

export default function useMobileTalbe(props: IMobileGameTableHooksProps) {
  const {
    pageId,
    dataSource,
    granularity,
    regions,
    genres,
    tableFormatter,
    defaultSortKey,
    defaultChekckNumberRange = [0, 5],
    countryMap,
    keyMap,
    handleNameClick,
  } = props;
  const source = {
    [dataSource]: ALL_METRICS,
  };

  const { filterExtend, setFilterExtend } = useFilterExtend({});

  const [loading, setIsLoading] = useState(true);
  const [originalTableData, setOriginalTableData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [fetchParams, setFetchParams] = useState<IMarketProfileMobilePageData>({
    source_metrics: source,
    granularity,
    // regions: regions?.includes('global') ? [] : regions,
    regions: [],
    platforms: ALL_PLATFORMS,
    metrics: ALL_METRICS,
    genres,
    b_growth_ratio: true,
    b_group_by_market: true,
  });

  const [tableAction, tableActionDispatch] = useReducer(reducer, {
    total: 0,
    pageIndex: 1,
    pageSize: 50,
    // key本身
    sortKey: defaultSortKey,
    // 比较值的话是_0,比较环比是_1
    sortKeyLabel: `${defaultSortKey}_0`,
    sortOrder: 'desc',
    selectedList: [],
    SelectionAll: [],
  });

  const newUpdataColors = ({
    list,
    colorsMap,
  }: {
    list: any[];
    colorsMap: typeof ECHARTSCOLOR;
  }) => {
    const newList = list?.map((item, idx) => {
      return {
        ...item,
        color: colorsMap[idx],
        colorRank: idx || 0,
      };
    });

    return newList;
  };

  const handleTableCheckboxChange = (list: any, status: string) => {
    // console.debug('onCheckBoxChange', { list, status });

    const isMoreThanTen = (_list) => _list.length >= MAX_CHECKED_NUMBER;

    let checked = list;

    switch (status) {
      case 'uncheck all':
        tableActionDispatch({ type: 'selectedList', payload: [] });
        break;
      case 'check all':
        if (tableAction.selectedList?.length > 0) {
          tableActionDispatch({ type: 'selectedList', payload: checked });
        } else {
          const hideCond = 'global,global_wo_china,ww';

          const noNeedCheckList = tableData?.filter((item) =>
            hideCond.split(',').includes(item?.market),
          );

          const offset = noNeedCheckList?.length || 0;

          const rawSelectedArr = tableData?.slice(offset, 6 + offset) || [];

          const top = newUpdataColors({ list: rawSelectedArr, colorsMap: ECHARTSCOLOR });

          tableActionDispatch({ type: 'selectedList', payload: top });
        }
        break;
      case 'uncheck': {
        const removedArr = tableAction.selectedList?.filter(
          (item) => item?.marketEn !== list.marketEn,
        );

        const updatedSelected = newUpdataColors({
          list: removedArr,
          colorsMap: ECHARTSCOLOR,
        });

        checked = updatedSelected;

        tableActionDispatch({ type: 'selectedList', payload: updatedSelected });
        break;
      }

      case 'check': {
        if (isMoreThanTen(tableAction.selectedList)) return;
        const newArr = tableAction.selectedList?.concat(list);

        const updatedSelected = newUpdataColors({
          list: newArr,
          colorsMap: ECHARTSCOLOR,
        });

        checked = updatedSelected;

        // console.debug('check', { updatedSelected, selectList: tableAction.selectedList });

        tableActionDispatch({ type: 'selectedList', payload: updatedSelected });
        break;
      }
      default:
        break;
    }
  };

  const getIsHideCol = (condStr: string, item, type: string) => {
    if (type === 'specicalContent') {
      // return !!condStr.includes(item?.market);
      return !!condStr.split(',').includes(item?.market);
    }
    return dataSource === condStr;
  };

  const handlePagingChange = () => {};

  const renderTableData = ({ tableList, checkList, cond }, isOuter?: boolean) => {
    // console.debug('renderTableData', { tableList, cond });

    if (!tableList) return;
    let { startDate, endDate, total } = cond;
    // if (!filter.startDate || (!filter.endDate && startDate && endDate)) {
    //   setDefaultDate({ startDate, endDate });
    // }
    if (!total) {
      total = tableList.length;
    }
    if (!checkList) {
      checkList = tableList;
    }
    setTableData(tableList);
    tableActionDispatch({ type: 'total', payload: total });
    // setIsLoading(false);
  };

  const tableFormat = ({ tableList, isInit, modifiedParams }) => {
    const sortKey = modifiedParams?.sortKey || tableAction.sortKey;
    const sortOrder = modifiedParams?.sortOrder || tableAction.sortOrder;
    const { original, formatted } = tableFormatter({
      fetchList: tableList,
      sortKey,
      sortOrder,
      dataSource,
      countryMap,
      isInit,
    });

    renderTableData({
      tableList: formatted,
      checkList: formatted,
      cond: {
        // startDate,
        // endDate,
        total: original?.length,
      },
    });

    // console.debug('renderTableData', formatted);

    setTableData(formatted);

    tableActionDispatch({
      type: 'pageIndex',
      payload: modifiedParams?.page || 1,
    });

    // console.debug('tableFormat', { formatted, original, sortKey, sortOrder, dataSource, isInit });

    if (isInit) {
      setOriginalTableData(original);
      const [startRange, endRange] = defaultChekckNumberRange;

      const hideCond = 'global,global_wo_china,ww';
      const noNeedCheckList = formatted?.filter((item) =>
        hideCond.split(',').includes(item?.market),
      );
      const offset = noNeedCheckList?.length || 0;

      const rawSelectedArr = formatted?.slice(startRange + offset, endRange + offset + 1) || [];

      const initList = newUpdataColors({
        list: rawSelectedArr,
        colorsMap: ECHARTSCOLOR,
      });

      // console.debug('addColorList', { addColorList, rawSelectedArr });

      tableActionDispatch({
        type: 'selectedList',
        payload: initList,
      });
    }

    return { original, formatted };
  };

  const handleTableSortChange = (e) => {
    const { key, sortVal } = e;
    // const sortKey = key.slice(0, key.length - 2);
    const baseKey = key.slice(0, key.length - 2);
    const isRatio = key.slice(key.length - 1) === '1';

    tableActionDispatch({
      type: 'sortKey',
      // payload: isRatio ? `${sortKey}_ratio` : sortKey,
      payload: isRatio ? `${baseKey}_ratio` : baseKey,
    });

    tableActionDispatch({
      type: 'sortKeyLabel',
      payload: key,
    });

    tableActionDispatch({
      type: 'sortOrder',
      payload: sortVal,
    });

    tableActionDispatch({
      type: 'pageIndex',
      payload: 1,
    });

    const { formatted } = tableFormat({
      tableList: originalTableData,
      isInit: false,
      modifiedParams: {
        // sortKey: sortVal,
        // sortOrder: isRatio ? `${sortKey}_ratio` : sortKey,
        sortKey: isRatio ? `${baseKey}_ratio` : baseKey,
        sortOrder: sortVal,
        page: 1,
      },
    });

    const [startRange, endRange] = defaultChekckNumberRange;

    const hideCond = 'global,global_wo_china,ww';
    const noNeedCheckList = formatted?.filter((item) => hideCond.split(',').includes(item?.market));
    const offset = noNeedCheckList?.length || 0;

    const rawSelectedArr = formatted?.slice(startRange + offset, endRange + offset + 1) || [];

    const updatedSelected = newUpdataColors({
      list: rawSelectedArr,
      colorsMap: ECHARTSCOLOR,
    });

    tableActionDispatch({
      type: 'selectedList',
      // payload: formatted?.slice(startRange + offset, endRange + offset + 1) || [],
      payload: updatedSelected,
    });
  };

  const handleFilteredArr = useCallback(
    _debounce((countries: CountryItem[] | '') => {
      if (countries === '' || !countries?.length) {
        // 恢复全量数据逻辑
        const { formatted } = tableFormatter({
          fetchList: originalTableData,
          sortKey: tableAction.sortKey,
          sortOrder: tableAction.sortOrder,
          dataSource,
          countryMap,
          isInit: false,
        });

        const hideCond = 'global,global_wo_china,ww';

        const noNeedCheckList = formatted?.filter((item) =>
          hideCond.split(',').includes(item?.market),
        );

        const offset = noNeedCheckList?.length || 0;

        const defaultSelected = formatted
          // .slice(defaultChekckNumberRange[0], defaultChekckNumberRange[1] + 1)
          .slice(defaultChekckNumberRange[0] + offset, defaultChekckNumberRange[1] + offset + 1);
        // .map((item, idx) => ({
        //   ...item,
        //   color: ECHARTSCOLOR[idx],
        // }));

        const updatedSelected = newUpdataColors({
          list: defaultSelected,
          colorsMap: ECHARTSCOLOR,
        });

        setTableData(formatted);
        tableActionDispatch({ type: 'selectedList', payload: updatedSelected });
        return;
      }

      // 筛选逻辑
      const filteredData = originalTableData?.filter((item: any) =>
        countries.some(
          (country) =>
            country.enName.toLowerCase() === item.market?.toLowerCase() ||
            country.name.toLowerCase() === item.marketEn?.toLowerCase(),
        ),
      );

      const { formatted } = tableFormatter({
        fetchList: filteredData,
        sortKey: tableAction.sortKey,
        sortOrder: tableAction.sortOrder,
        dataSource,
        countryMap,
        isInit: false,
      });

      // const updatedSelected = formatted.map((item, idx) => ({
      //   ...item,
      //   color: ECHARTSCOLOR[idx],
      // }));

      const updatedSelected = newUpdataColors({
        list: formatted,
        colorsMap: ECHARTSCOLOR,
      });

      setTableData(formatted);
      tableActionDispatch({ type: 'selectedList', payload: updatedSelected });
    }, 500),
    [originalTableData, tableAction.sortKey, tableAction.sortOrder, dataSource, countryMap],
  );

  const updateData = ({
    params,
    isInit = false,
  }: {
    params: IMarketProfileMobilePageData;
    isInit?: boolean;
  }) => {
    setIsLoading(true);

    getMobilePageData(params)
      .then((res: any) => {
        const tableData = res?.data || {};

        if (isInit) {
          const { start_date: startDate, end_date: endDate, granularity } = res?.req || {};

          tableFormat({
            tableList: tableData?.metric_value_list || [],
            isInit: true,
            modifiedParams: {
              startDate,
              endDate,
            },
          });

          setFetchParams({
            ...fetchParams,
            ...params,
            start_date: startDate,
            end_date: endDate,
          });

          setFilterExtend({
            ...filterExtend,
            rangeTime: {
              type: feachTimeTypeToFilterTimeType(granularity),
              value: [startDate, endDate],
            },
          });
        } else {
          tableFormat({
            tableList: tableData?.metric_value_list || [],
            isInit: true,
            modifiedParams: {
              startDate: params?.start_date,
              endDate: params?.end_date,
            },
          });

          setFetchParams({
            ...fetchParams,
            ...params,
          });
        }

        setIsLoading(false);
      })
      .catch(() => {
        renderTableData({
          tableList: [],
          checkList: [],
          cond: {},
        });

        setIsLoading(false);
      });
  };

  /**
   * Initializes mobile page data by fetching and formatting table data based on specified parameters.
   * Handles data loading state and error cases.
   *
   * @remarks
   * - Requires countryMap to be populated
   * - Sets loading state during data fetch
   * - Formats table data with start/end dates upon successful response
   * - Clears table data on error
   */
  const initial = () => {
    // setIsLoading(true);

    // console.log('isOperateisOperate22224444', isOperate);
    // console.log('filterExtendfilterExtend22224444', filterExtend);
    // let fetchParamsTemp = { ...fetchParams };
    // if (isOperate) {
    //   fetchParamsTemp = {
    //     ...fetchParamsTemp,
    //     start_date: filterExtend?.rangeTime?.start_date || '',
    //     end_date: filterExtend?.rangeTime?.end_date || '',
    //   };
    // }
    updateData({
      params: fetchParams,
      // params: fetchParamsTemp,
      isInit: true,
    });
  };

  useDeepCompareEffect(() => {
    if (!countryMap?.length) return;
    setIsLoading(true);

    initial();
  }, [countryMap]);

  useEffect(() => {
    return () => {
      // @ts-ignore
      handleFilteredArr.cancel(); // 清理未执行的防抖函数
    };
  }, []);

  return {
    fetchParams,
    isTableLoading: loading,
    tableData,
    tableAction,
    tableActionDispatch,
    updateData,
    getIsHideCol,
    handleTableCheck: handleTableCheckboxChange,
    handlePagingChange,
    handleTableSortChange,
    handleFilteredArr,
  };
}
